import React, { useState, useEffect } from 'react';
import { Mail, Phone, Linkedin, Globe, MapPin, Building2, Users, Calendar, User, Pencil, Briefcase, ArrowRight, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Meeting } from '../../../shared/types';
import { StatusSelect } from '../../../components/ui/StatusSelect';
import { TransferStatusSelect } from '../../../components/ui/TransferStatusSelect';
import { ConversationViewer } from '../../../components/ui/ConversationViewer';
import { DateTimePicker } from '../../../components/ui/DateTimePicker';

export type Tab = 'contact' | 'conversation' | 'notes';

interface EditableFields {
  Nombre: string;
  Title: string;
  Empresa: string;
  Email: string;
  Teléfono: string;
  'Teléfono móvil': string;
  Linkedin: string;
  'Página Web': string;
  Localización: string;
  Sector: string;
  Empleados: string;
  Resumen: string;
  onDateUpdate: (date: string) => Promise<void>;
}

interface MeetingModalProps {
  meeting: Meeting;
  onClose: () => void;
  activeTab: Tab;
  setActiveTab: (tab: Tab) => void;
  onStatusUpdate: (status: string | null) => void;
  onNotesUpdate: (notes: string) => Promise<void>;
  onContactUpdate: (fields: EditableFields) => Promise<void>;
  isOpen: boolean;
  onDateUpdate: (date: string) => Promise<void>;
}

// Define props for ContactInfo
interface ContactInfoProps {
  editableFields: EditableFields;
  handleFieldChange: (field: keyof EditableFields, value: string) => void;
  handleContactSave: () => Promise<void>;
  isSavingContact: boolean;
  contactSaveSuccess: boolean;
  meetingFields: Meeting['fields']; // For 'Preguntas Calendly'
  isEditMode: boolean;
  setIsEditMode: (value: boolean) => void;
  originalFields: EditableFields;
  /** When true, render the Promote-to-Direct-Call CTA next to the Edit
   *  button. Only set for rows where meeting_type='interested_transfer'. */
  isTransferRow: boolean;
  onRequestPromote: () => void;
}

// Define EditableField component props
interface EditableFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  icon?: React.ElementType;
  type?: string;
  required?: boolean;
  isEditMode?: boolean;
}

// Define EditableField component outside MeetingModal
const EditableField: React.FC<EditableFieldProps> = ({
  label,
  value,
  onChange,
  icon: Icon,
  type = 'text',
  required,
  isEditMode = false
}) => {
  const { t } = useTranslation();

  // Helper function to determine if value is a URL or field is URL-related
  const isUrl = (str: string, fieldLabel?: string) => {
    if (!str) return false;
    // Check if field label indicates it's a URL field
    if (fieldLabel && (fieldLabel.toLowerCase().includes('web') || fieldLabel.toLowerCase().includes('linkedin'))) {
      return true;
    }
    // Check if value looks like a URL
    return str.startsWith('http://') || str.startsWith('https://') || str.includes('linkedin.com') || str.includes('www.') || str.includes('.com') || str.includes('.es');
  };

  // Helper function to handle click on URL fields
  const handleUrlClick = () => {
    if (!isEditMode && isUrl(value, label)) {
      const url = value.startsWith('http') ? value : `https://${value}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div>
      <div className="text-xs font-medium text-gray-500 mb-1">{label}</div>
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-gray-400" />}
        {isEditMode ? (
          <input
            type={type}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            required={required}
            className="flex-1 px-2 py-1 text-sm border border-gray-200 focus:outline-none focus:ring-1 focus:ring-[#0055FF] focus:border-[#0055FF]"
          />
        ) : (
          <div
            className={`flex-1 px-2 py-1 text-sm min-h-[1.75rem] flex items-center ${
              isUrl(value, label)
                ? 'text-blue-600 hover:text-blue-800 cursor-pointer hover:underline'
                : 'text-gray-900'
            }`}
            onClick={handleUrlClick}
          >
            {value || t('meetings.notSpecified')}
          </div>
        )}
      </div>
    </div>
  );
};

// Define ContactInfo component outside MeetingModal
const ContactInfo: React.FC<ContactInfoProps> = ({
  editableFields,
  handleFieldChange,
  handleContactSave,
  isSavingContact,
  contactSaveSuccess,
  meetingFields,
  isEditMode,
  setIsEditMode,
  originalFields,
  isTransferRow,
  onRequestPromote,
}) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-8 relative">
      {/* Contact Information Section */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
          {t('meetings.contactInfo')}
        </h3>
        <div className="space-y-4">
          <EditableField
            label={t('meetings.name')}
            value={editableFields.Nombre}
            onChange={(value: string) => handleFieldChange('Nombre', value)}
            icon={User}
            required
            isEditMode={isEditMode}
          />
          <EditableField
            label={t('meetings.jobTitle')}
            value={editableFields.Title}
            onChange={(value: string) => handleFieldChange('Title', value)}
            icon={Briefcase}
            isEditMode={isEditMode}
          />
          <EditableField
            label={t('meetings.email')}
            value={editableFields.Email}
            onChange={(value: string) => handleFieldChange('Email', value)}
            icon={Mail}
            type="email"
            isEditMode={isEditMode}
          />
          <EditableField
            label={t('meetings.phone')}
            value={editableFields.Teléfono}
            onChange={(value: string) => handleFieldChange('Teléfono', value)}
            icon={Phone}
            isEditMode={isEditMode}
          />
          <EditableField
            label={t('meetings.mobilePhone')}
            value={editableFields['Teléfono móvil']}
            onChange={(value: string) => handleFieldChange('Teléfono móvil', value)}
            icon={Phone}
            isEditMode={isEditMode}
          />
          <EditableField
            label={t('meetings.linkedin')}
            value={editableFields.Linkedin}
            onChange={(value: string) => handleFieldChange('Linkedin', value)}
            icon={Linkedin}
            isEditMode={isEditMode}
          />
        </div>
      </div>

      {/* Business/Company Information Section */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
          {t('meetings.businessInfo')}
        </h3>
        <div className="space-y-4">
          <EditableField
            label={t('meetings.company')}
            value={editableFields.Empresa}
            onChange={(value: string) => handleFieldChange('Empresa', value)}
            icon={Building2}
            required
            isEditMode={isEditMode}
          />
          <div>
            <div className="text-xs font-medium text-gray-500 mb-1">{t('meetings.summary')}</div>
            <div className="flex items-start gap-2">
              <Pencil className="w-4 h-4 text-gray-400 mt-1.5" />
              {isEditMode ? (
                <textarea
                  value={editableFields.Resumen}
                  onChange={(e) => handleFieldChange('Resumen', e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-200 focus:outline-none focus:ring-1 focus:ring-[#0055FF] focus:border-[#0055FF] min-h-[100px] resize-none"
                  placeholder={t('meetings.summaryPlaceholder')}
                />
              ) : (
                <div className="w-full px-2 py-1 text-sm min-h-[100px] text-gray-900">
                  {editableFields.Resumen || t('meetings.notSpecified')}
                </div>
              )}
            </div>
          </div>
          <EditableField
            label={t('meetings.sector')}
            value={editableFields.Sector}
            onChange={(value: string) => handleFieldChange('Sector', value)}
            icon={Building2}
            isEditMode={isEditMode}
          />
          <EditableField
            label={t('meetings.employees')}
            value={editableFields.Empleados}
            onChange={(value: string) => handleFieldChange('Empleados', value)}
            icon={Users}
            type="number"
            isEditMode={isEditMode}
          />
          <EditableField
            label={t('meetings.location')}
            value={editableFields.Localización}
            onChange={(value: string) => handleFieldChange('Localización', value)}
            icon={MapPin}
            isEditMode={isEditMode}
          />
          <EditableField
            label={t('meetings.website')}
            value={editableFields['Página Web']}
            onChange={(value: string) => handleFieldChange('Página Web', value)}
            icon={Globe}
            isEditMode={isEditMode}
          />
          <MultilineField label={t('meetings.calendlyQuestions')} value={(meetingFields as Record<string, unknown>)['Preguntas Calendly'] as string || ''} />
        </div>
      </div>
      <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200 gap-2">
        <div className="flex items-center space-x-2">
          <button
            onClick={handleContactSave}
            disabled={isSavingContact}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {isSavingContact ? t('meetings.saving') : isEditMode ? t('common.save') : t('common.edit')}
          </button>

          {/* Cancel button - only show in edit mode */}
          {isEditMode && (
            <button
              onClick={() => {
                setIsEditMode(false);
                // Reset fields to original values
                Object.keys(originalFields).forEach(key => {
                  handleFieldChange(key as keyof EditableFields, originalFields[key as keyof EditableFields] as string);
                });
              }}
              disabled={isSavingContact}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 transition-colors"
            >
              {t('common.cancel')}
            </button>
          )}

          {contactSaveSuccess && <div className="w-[0.375rem] h-[0.375rem] bg-green-600 ml-2"></div>}
        </div>

        {/* Promote CTA — only for Interested transfers. High-contrast styling
            to stand out from the Edit button next to it. */}
        {isTransferRow && !isEditMode && (
          <button
            type="button"
            onClick={onRequestPromote}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-gray-900 hover:bg-black transition-colors"
          >
            <ArrowRight className="w-3.5 h-3.5" />
            {t('promote.buttonLabel')}
          </button>
        )}
      </div>
    </div>
  );
};

// Define props for TabButton
interface TabButtonProps {
  tab: Tab;
  label: string;
  activeTab: Tab;
  setActiveTab: (tab: Tab) => void;
}

// Define TabButton component outside MeetingModal
const TabButton: React.FC<TabButtonProps> = ({ tab, label, activeTab, setActiveTab }) => (
  <button
    onClick={() => setActiveTab(tab)}
    className={`px-6 py-2.5 text-sm font-medium transition-colors text-left ${
      activeTab === tab
        ? 'text-gray-900 bg-white border-b-2 border-gray-900'
        : 'text-gray-500 hover:text-gray-700'
    }`}
  >
    {label}
  </button>
);

export const MeetingModal: React.FC<MeetingModalProps> = ({
  meeting,
  onClose,
  onStatusUpdate,
  onNotesUpdate,
  onContactUpdate,
  activeTab,
  setActiveTab,
  isOpen,
  onDateUpdate
}) => {
  const { t } = useTranslation();

  const [notes, setNotes] = useState('');
  const [isSavingContact, setIsSavingContact] = useState(false);
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [contactSaveSuccess, setContactSaveSuccess] = useState(false);
  const [notesSaveSuccess, setNotesSaveSuccess] = useState(false);
  const [localStatus, setLocalStatus] = useState<string | null>(meeting.fields['Status Lead'] || null);
  const [localTransferStatus, setLocalTransferStatus] = useState<string | null>(
    meeting.fields.transfer_status ?? null,
  );
  const [isEditMode, setIsEditMode] = useState(false);
  const [showPromoteConfirm, setShowPromoteConfirm] = useState(false);
  const [isPromoting, setIsPromoting] = useState(false);

  const isTransferRow = meeting.fields.meeting_type === 'interested_transfer';
  const [editableFields, setEditableFields] = useState<EditableFields>(() => ({
    Nombre: meeting.fields.Nombre || '',
    Title: (meeting.fields as Record<string, unknown>).Title as string || '',
    Empresa: meeting.fields.Empresa || '',
    Email: meeting.fields.Email || '',
    Teléfono: meeting.fields.Teléfono || '',
    'Teléfono móvil': meeting.fields['Teléfono móvil'] || '',
    Linkedin: meeting.fields.Linkedin || '',
    'Página Web': meeting.fields['Página Web'] || '',
    Localización: meeting.fields.Localización || '',
    Sector: meeting.fields.Sector || '',
    Empleados: meeting.fields.Empleados?.toString() || '',
    Resumen: meeting.fields.Resumen || '',
    onDateUpdate: onDateUpdate
  }));

  // Update editable fields when meeting changes
  useEffect(() => {
    setEditableFields({
      Nombre: meeting.fields.Nombre || '',
      Title: (meeting.fields as Record<string, unknown>).Title as string || '',
      Empresa: meeting.fields.Empresa || '',
      Email: meeting.fields.Email || '',
      Teléfono: meeting.fields.Teléfono || '',
      'Teléfono móvil': meeting.fields['Teléfono móvil'] || '',
      Linkedin: meeting.fields.Linkedin || '',
      'Página Web': meeting.fields['Página Web'] || '',
      Localización: meeting.fields.Localización || '',
      Sector: meeting.fields.Sector || '',
      Empleados: meeting.fields.Empleados?.toString() || '',
      Resumen: meeting.fields.Resumen || '',
      onDateUpdate: onDateUpdate
    });
  }, [meeting.fields, onDateUpdate]);
  // Update notes when meeting changes
  React.useEffect(() => {
    setNotes(meeting.fields.Notas || '');
  }, [meeting.fields.Notas]);

  // Update local status when meeting changes
  React.useEffect(() => {
    setLocalStatus(meeting.fields['Status Lead'] || null);
    setLocalTransferStatus(meeting.fields.transfer_status ?? null);
  }, [meeting.fields]);

  if (!isOpen) return null;

  // const handleClickOutside = (e: React.MouseEvent) => {
  //   if (e.target === e.currentTarget) {
  //     onClose();
  //   }
  // };

  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNotes(e.target.value);
  };

  const handleNotesSave = async () => {
    setIsSavingNotes(true);
    const minLoadingPromise = new Promise(resolve => setTimeout(resolve, 1000));
    try {
      await Promise.all([
        onNotesUpdate(notes),
        minLoadingPromise
      ]);
      setNotesSaveSuccess(true);
      setTimeout(() => setNotesSaveSuccess(false), 1500);
    } catch (error) {
      console.error("Error saving notes:", error);
    } finally {
      setIsSavingNotes(false);
    }
  };

  const handleFieldChange = (field: keyof EditableFields, value: string) => {
    setEditableFields(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Promote handler — confirmation modal triggers this. Targets
  // 'transferred_meeting' rather than 'direct_call' so the row stays
  // channel-agnostic (the client may keep contact via email, phone, or
  // proposals). meeting_type + promoted_at flip in one update; is_direct_call
  // stays false. 500ms min loading — handleMeetingUpdate is optimistic so the
  // background reflects the move instantly; a longer wait just widens the
  // overlap window.
  const handlePromoteConfirm = async () => {
    setIsPromoting(true);
    const minLoadingPromise = new Promise(resolve => setTimeout(resolve, 500));
    try {
      await Promise.all([
        onContactUpdate({
          meeting_type: 'transferred_meeting',
          is_direct_call: false,
          promoted_at: new Date().toISOString(),
        } as unknown as EditableFields),
        minLoadingPromise,
      ]);
      setShowPromoteConfirm(false);
      onClose();
    } catch (err) {
      console.error('Promote failed:', err);
      setIsPromoting(false);
    }
  };

  const handleContactSave = async () => {
    // If not in edit mode, switch to edit mode
    if (!isEditMode) {
      setIsEditMode(true);
      return;
    }

    // If in edit mode, save the changes
    setIsSavingContact(true);
    const minLoadingPromise = new Promise(resolve => setTimeout(resolve, 1000));
    try {
      // Create updates object excluding the onDateUpdate function
      const { onDateUpdate: _, ...fieldsWithoutFunction } = editableFields;
      const updates = {
        ...fieldsWithoutFunction,
        Title: editableFields.Title,
        Empleados: editableFields.Empleados ? parseInt(editableFields.Empleados) : undefined
      };
      await Promise.all([
        onContactUpdate(updates as Record<string, unknown>),
        minLoadingPromise
      ]);
      setContactSaveSuccess(true);
      setIsEditMode(false); // Switch back to view mode after successful save
      setTimeout(() => setContactSaveSuccess(false), 1500);
    } catch (error) {
      console.error("Error saving contact info:", error);
    } finally {
      setIsSavingContact(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end pointer-events-none"
    >
      <div
        className="bg-white w-[40%] h-full shadow-xl pointer-events-auto relative"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center space-x-2">
                <h2 className="text-lg font-medium text-gray-900">{meeting.fields.Nombre}</h2>
                <span className="text-gray-400">·</span>
                <span className="text-gray-500">{meeting.fields.Empresa}</span>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500 p-1 hover:bg-gray-50 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-2.5">
              <div className="flex items-center text-sm text-gray-600">
                <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                <DateTimePicker
                  value={meeting.fields['Fecha Reunión'] || ''}
                  onChange={async (dateString) => {
                    if (dateString) {
                      await onDateUpdate(dateString);
                    }
                  }}
                  allowClear={false}
                />
              </div>
              {isTransferRow ? (
                <TransferStatusSelect
                  value={localTransferStatus}
                  onChange={async (status) => {
                    setLocalTransferStatus(status);
                    try {
                      await onContactUpdate({ transfer_status: status } as unknown as EditableFields);
                    } catch (error) {
                      console.error('Error updating transfer status:', error);
                      setLocalTransferStatus(meeting.fields.transfer_status ?? null);
                    }
                  }}
                />
              ) : (
                <StatusSelect
                  value={localStatus}
                  onChange={async (status) => {
                    setLocalStatus(status);
                    try {
                      await onStatusUpdate(status);
                    } catch (error) {
                      console.error('Error updating status:', error);
                      setLocalStatus(meeting.fields['Status Lead'] || null);
                    }
                  }}
                />
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <div className="flex">
              <TabButton tab="contact" label={t('meetings.contact')} activeTab={activeTab} setActiveTab={setActiveTab} />
              <TabButton tab="conversation" label={t('meetings.conversation')} activeTab={activeTab} setActiveTab={setActiveTab} />
              <TabButton tab="notes" label={t('meetings.notes')} activeTab={activeTab} setActiveTab={setActiveTab} />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-8 py-6">
            {activeTab === 'contact' && <ContactInfo
              editableFields={editableFields}
              handleFieldChange={handleFieldChange}
              handleContactSave={handleContactSave}
              isSavingContact={isSavingContact}
              contactSaveSuccess={contactSaveSuccess}
              meetingFields={meeting.fields}
              isEditMode={isEditMode}
              setIsEditMode={setIsEditMode}
              originalFields={{
                Nombre: meeting.fields.Nombre || '',
                Title: (meeting.fields as Record<string, unknown>).Title as string || '',
                Empresa: meeting.fields.Empresa || '',
                Email: meeting.fields.Email || '',
                Teléfono: meeting.fields.Teléfono || '',
                'Teléfono móvil': meeting.fields['Teléfono móvil'] || '',
                Linkedin: meeting.fields.Linkedin || '',
                'Página Web': meeting.fields['Página Web'] || '',
                Localización: meeting.fields.Localización || '',
                Sector: meeting.fields.Sector || '',
                Empleados: meeting.fields.Empleados?.toString() || '',
                Resumen: meeting.fields.Resumen || '',
                onDateUpdate: onDateUpdate
              }}
              isTransferRow={isTransferRow}
              onRequestPromote={() => setShowPromoteConfirm(true)}
            />}
            {activeTab === 'conversation' && (
              <div className="space-y-2">
                <ConversationViewer
                  messageRecordIds={meeting.fields['Link to Missatges (from Link Inbox)']}
                  leadName={meeting.fields.Nombre}
                />
              </div>
            )}
            {activeTab === 'notes' && (
              <div className="space-y-3">
                <textarea
                  value={notes}
                  onChange={handleNotesChange}
                  className="w-full h-64 px-3 py-2 text-sm text-gray-600 border border-gray-200 focus:ring-1 focus:ring-gray-900 focus:border-gray-900 resize-none"
                  placeholder={t('meetings.addNotes') + '...'}
                />
                <div className="flex items-center">
                  <button
                    onClick={handleNotesSave}
                    disabled={isSavingNotes}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    {isSavingNotes ? t('meetings.savingNotes') : t('meetings.saveNotes')}
                  </button>
                  {notesSaveSuccess && <div className="w-[0.375rem] h-[0.375rem] bg-green-600 ml-2"></div>}
                </div>
                {meeting.fields.notes_updated_by && meeting.fields.notes_updated_at && (
                  <p className="text-xs text-gray-400">
                    {t('meetings.notesLastEdited', {
                      by: meeting.fields.notes_updated_by,
                      when: new Date(meeting.fields.notes_updated_at).toLocaleString(),
                    })}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Promote confirmation overlay — replaces window.confirm, gives a 2s
          minimum loading state so the action feels deliberate. */}
      {showPromoteConfirm && (
        <PromoteConfirmOverlay
          isProcessing={isPromoting}
          onCancel={() => {
            if (isPromoting) return;
            setShowPromoteConfirm(false);
          }}
          onConfirm={handlePromoteConfirm}
        />
      )}
    </div>
  );
};

interface PromoteConfirmOverlayProps {
  isProcessing: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

const PromoteConfirmOverlay: React.FC<PromoteConfirmOverlayProps> = ({ isProcessing, onCancel, onConfirm }) => {
  const { t } = useTranslation();
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-40 pointer-events-auto"
      onClick={onCancel}
    >
      <div
        className="bg-white border border-gray-200 shadow-xl w-[440px]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">{t('promote.confirmTitle')}</h3>
          <p className="text-sm text-gray-700 leading-relaxed">{t('promote.confirmBody')}</p>
        </div>
        <div className="px-6 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={isProcessing}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-40 transition-colors"
          >
            {t('promote.cancelAction')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isProcessing}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-semibold text-white bg-gray-900 hover:bg-black disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            {isProcessing && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {isProcessing ? t('promote.processing') : t('promote.confirmAction')}
          </button>
        </div>
      </div>
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const InfoField = ({
  label,
  value,
  icon: Icon,
  isLink,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  alwaysShow = false
}: {
  label: string;
  value?: string | null;
  icon?: React.ElementType;
  isLink?: boolean;
  alwaysShow?: boolean;
}) => {
  return (
    <div>
      <div className="text-xs font-medium text-gray-500 mb-1">{label}</div>
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-gray-400" />}
        {isLink ? (
          value ? (
          <a
            href={value.startsWith('http') ? value : `https://${value}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-gray-900 hover:underline"
          >
            {value}
          </a>
          ) : (
            <span className="text-sm text-gray-400">Sin especificar</span>
          )
        ) : (
          <span className="text-sm text-gray-600">{value || <span className="text-gray-400">Sin especificar</span>}</span>
        )}
      </div>
    </div>
  );
};

const MultilineField = ({
  label,
  value
}: {
  label: string;
  value?: string | null;
}) => {
  if (!value) return null;

  return (
    <div>
      <div className="text-xs font-medium text-gray-500 mb-1">{label}</div>
      <div className="text-sm text-gray-600 whitespace-pre-wrap leading-relaxed bg-gray-50 p-4">
        {value}
      </div>
    </div>
  );
};